import { zodResolver } from '@hookform/resolvers/zod'
import { AlertTriangle, FileSignature, Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Modal } from '@/components/ui/modal'
import { FormSelect } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { todayIso } from '../../taskFilters'
import { DOC_STATUSES, SEVERITIES, type Installation, type Project } from '../../types'
import { toastError, useStaffLookup } from '../../ui'
import { useInstallationActions } from '../../workApi'

type Props = { project: Project; installation: Installation; onDone: () => void }

function Footer({ pending, onCancel }: { pending: boolean; onCancel: () => void }) {
  const { t } = useTranslation()
  return (
    <>
      <Button type="button" variant="cancel" onClick={onCancel}>
        {t('common.cancel')}
      </Button>
      <Button type="submit" disabled={pending}>
        {pending ? <Loader2 className="animate-spin" /> : null}
        {t('common.save')}
      </Button>
    </>
  )
}

function Field({ id, label, error, children }: { id: string; label: string; error?: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  )
}

export function BlockerForm({ project, installation, onDone }: Props) {
  const { t } = useTranslation()
  const { saveBlocker } = useInstallationActions(project.id, installation.id)
  const { nameOf } = useStaffLookup()
  const schema = z.object({
    text: z.string().trim().min(5, t('projects.install.form.blockerRequired')).max(240),
    severity: z.enum(SEVERITIES),
    ownerId: z.string(),
    date: z.string().min(1, t('projects.form.dateRequired')),
  })
  const form = useForm({ resolver: zodResolver(schema), defaultValues: { text: '', severity: 'medium' as const, ownerId: '', date: todayIso() } })
  const { errors } = form.formState

  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await saveBlocker.mutateAsync({ ...v, ownerId: v.ownerId || null })
      toast.success(t('projects.install.blockerCreated'))
      onDone()
    } catch (e) {
      toastError(e)
    }
  })

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onDone()}
      dismissible={!saveBlocker.isPending}
      icon={<AlertTriangle />}
      title={t('projects.install.form.blockerTitle')}
      description={t('projects.install.form.blockerHint', { name: installation.customer })}
      onSubmit={(e) => void onSubmit(e)}
      footer={<Footer pending={saveBlocker.isPending} onCancel={onDone} />}
    >
      <div className="space-y-4">
        <Field id="bl-text" label={t('projects.install.form.blockerText')} error={errors.text?.message}>
          <Textarea id="bl-text" rows={3} aria-invalid={!!errors.text} {...form.register('text')} />
        </Field>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field id="bl-sev" label={t('projects.install.form.severity')}>
            <FormSelect id="bl-sev" control={form.control} name="severity" options={SEVERITIES.map((s) => ({ value: s, label: t(`projects.severity.${s}`) }))} />
          </Field>
          <Field id="bl-owner" label={t('projects.install.columns.owner')}>
            <FormSelect id="bl-owner" control={form.control} name="ownerId" options={[{ value: '', label: t('projects.work.unassigned') }, ...project.team.map((m) => ({ value: m.userId, label: nameOf(m.userId) }))]} />
          </Field>
          <Field id="bl-date" label={t('projects.install.form.date')} error={errors.date?.message}>
            <Controller control={form.control} name="date" render={({ field }) => <DateField id="bl-date" value={field.value} onChange={field.onChange} clearable={false} />} />
          </Field>
        </div>
      </div>
    </Modal>
  )
}

export function DocForm({ project, installation, onDone }: Props) {
  const { t } = useTranslation()
  const { saveDoc } = useInstallationActions(project.id, installation.id)
  const schema = z.object({
    name: z.string().trim().min(2, t('projects.install.form.docRequired')).max(80),
    date: z.string().min(1, t('projects.form.dateRequired')),
    status: z.enum(DOC_STATUSES),
  })
  const form = useForm({ resolver: zodResolver(schema), defaultValues: { name: '', date: installation.targetDate, status: 'waiting' as const } })
  const { errors } = form.formState

  const onSubmit = form.handleSubmit(async (input) => {
    try {
      await saveDoc.mutateAsync({ input })
      toast.success(t('projects.install.docCreated'))
      onDone()
    } catch (e) {
      toastError(e)
    }
  })

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onDone()}
      dismissible={!saveDoc.isPending}
      icon={<FileSignature />}
      title={t('projects.install.form.docTitle')}
      description={t('projects.install.form.docHint', { name: installation.customer })}
      onSubmit={(e) => void onSubmit(e)}
      footer={<Footer pending={saveDoc.isPending} onCancel={onDone} />}
    >
      <div className="space-y-4">
        <Field id="dc-name" label={t('projects.install.form.docName')} error={errors.name?.message}>
          <Input id="dc-name" aria-invalid={!!errors.name} {...form.register('name')} />
        </Field>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field id="dc-date" label={t('projects.install.form.date')} error={errors.date?.message}>
            <Controller control={form.control} name="date" render={({ field }) => <DateField id="dc-date" value={field.value} onChange={field.onChange} clearable={false} />} />
          </Field>
          <Field id="dc-status" label={t('projects.install.columns.status')}>
            <FormSelect id="dc-status" control={form.control} name="status" options={DOC_STATUSES.map((s) => ({ value: s, label: t(`projects.docStatus.${s}`) }))} />
          </Field>
        </div>
      </div>
    </Modal>
  )
}
